import React from "react";
import styled, { css } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

import { colors } from "utils";
import { levelLabel, soloBand, soloTimeText } from "./studentReportTable";

// The Student Reports table, as the design draws it — extracted from the
// Session Detail tab so the Activity & Collaboration Trend tab renders the SAME
// table rather than a second one that looks like it. The design uses one Table
// component across both frames (Row/Header and Row/Body); two copies of these
// measurements would drift the first time one of them was adjusted.

// Sideways scrolling only. `overflow-x: auto` silently turns overflow-y to auto
// too, and a table whose height lands on half a pixel (row borders do that)
// then holds a pixel of vertical scroll on a retina screen -- which macOS latches
// a wheel gesture to, so the page stops scrolling mid-table.
export const TableWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
`;

// The header carries the design's own measurements (frame 2637:48693): Inter
// Bold 16/22 at 0.25px, 8px of padding, and the sort control pushed to the
// cell's right edge rather than sitting beside the label.
export const ColumnHeader = styled.th`
  background-color: ${colors.nightlyWoods500};
  color: #ffffff;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
  padding: 8px;
  text-align: left;
  /* The two Level headers stack a Rubric link under their sort control, so the
     row is two lines tall; this keeps the other headers centred in it. */
  vertical-align: middle;
  white-space: nowrap;
`;

export const SortableHeader = styled(ColumnHeader)`
  padding: 0;
`;

export const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  /* The font shorthand does not carry letter-spacing, so the header's 0.25px
     is inherited on its own. */
  letter-spacing: inherit;
  cursor: pointer;

  /* Opt-in: a 24px spacer, the label centred and free to wrap, and the icon at
     the right edge, so the label sits on the cell's centre line. */
  ${(props) =>
    props.$centered &&
    css`
      justify-content: space-between;
      gap: 0;

      /* The label takes only the space left over, so at the design's width
         the spacer and the icon keep their 24px. In a narrower column the
         spacer gives way before the label's longest word or the icon would
         be pushed out of the cell. */
      &::before {
        content: "";
        flex: 0 1 24px;
        width: 24px;
      }

      & > span {
        flex: 1 1 0;
        text-align: center;
        white-space: normal;
      }

      /* A wrapping label must not squeeze the 24px icon. */
      & > svg {
        flex-shrink: 0;
      }
    `}

  /* Opt-in on the centred layout: the label starts right after the spacer. */
  ${(props) =>
    props.$centered &&
    props.$labelAlign === "left" &&
    css`
      & > span {
        text-align: left;
      }
    `}
`;

export const SortIcon = styled(FontAwesomeIcon)`
  width: 24px;
  height: 24px;
  font-size: 18px;
  flex-shrink: 0;
  opacity: ${(props) => (props.$isActive ? 1 : 0.5)};
`;

/**
 * The design's sort glyph: a white up triangle over a white down triangle, at
 * full opacity. Sorted ascending it keeps only the up triangle, descending only
 * the down one.
 */
export const SortGlyph = ({ direction = null }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {direction !== "desc" && <path d="M12 3L17.1962 10.5H6.80385L12 3Z" fill="#FFFFFF" />}
    {direction !== "asc" && <path d="M12 21L17.1962 13.5H6.80385L12 21Z" fill="#FFFFFF" />}
  </svg>
);

// A row that opens nothing keeps its own background on hover.
export const BodyRow = styled.tr`
  cursor: ${(props) => (props.$clickable === false ? "default" : "pointer")};

  ${(props) =>
    props.$clickable !== false &&
    css`
      &:hover {
        background-color: ${colors.blue50 || "#F4F4FD"};
      }
    `}
`;

// Body cells to the design's own measurements: 8px of padding, Inter Regular
// 16/22 at 0.25px, and the rule UNDER each row rather than over it — the
// design draws `green/400` on the cell's bottom edge.
export const Cell = styled.td`
  padding: 8px;
  border-bottom: 1px solid #858b87;
  vertical-align: middle;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
`;

// Tag - Level/Large, as the design defines it: a tinted fill inside a matching
// 1px border, not a bare fill. Levels 1 and 4 carry a glyph — the warning that
// marks the level needing support, and the star that marks the top one.
export const LEVEL_COLORS = {
  1: { bg: "#FFF2F5", border: "#FEADC3", fg: "#BC165D", icon: faTriangleExclamation },
  2: { bg: "#FEFCE8", border: "#EAB308", fg: "#9B6B03", icon: null },
  3: { bg: "#E9FFF5", border: "#76D9AD", fg: "#006B3B", icon: null },
  4: { bg: "#F4F4FD", border: "#BAB9F5", fg: "#4745AE", icon: faStar },
};

// The Solo Accuracy pill borrows the level palette: its high, mid and
// low bands wear Level 3, 2 and 1.
export const SOLO_BAND_LEVEL = { high: 3, mid: 2, low: 1 };

const LevelPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  /* The frame's 4px of padding with its 1px border stroked INSIDE the 30px
     tag. CSS adds the border outside, so the height is fixed at 30 and the
     22px line is centred in it: the text sits where 3px of padding put it. */
  box-sizing: border-box;
  height: 30px;
  padding: 4px 8px;
  border-radius: 11px;
  white-space: nowrap;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.25px;
  background-color: ${(props) => LEVEL_COLORS[props.$level]?.bg || "transparent"};
  border: 1px solid ${(props) => LEVEL_COLORS[props.$level]?.border || "transparent"};
  color: ${(props) => LEVEL_COLORS[props.$level]?.fg || colors.nightlyWoods900};
`;

const LevelIcon = styled(FontAwesomeIcon)`
  flex-shrink: 0;
  font-size: 14px;
`;

/**
 * One level tag: the glyph the level carries, then its label. `noStar` drops
 * Level 4's star and nothing else, for the tables whose design draws it bare.
 */
export const LevelTag = ({ level, noStar = false }) => {
  const icon = LEVEL_COLORS[level]?.icon;
  const showIcon = icon && !(noStar && icon === faStar);
  return (
    <LevelPill $level={level}>
      {showIcon && <LevelIcon icon={icon} aria-hidden />}
      {levelLabel(level)}
    </LevelPill>
  );
};

/**
 * The Solo Accuracy pill: "N of M" in its band's colours, never an icon. An
 * unknown has no band, and it is the bare em dash rather than an empty pill.
 */
export const SoloPill = ({ row }) => {
  const band = soloBand(row);
  if (!band) return soloTimeText(row);
  return (
    <LevelPill $level={SOLO_BAND_LEVEL[band]} data-testid="solo-pill">
      {soloTimeText(row)}
    </LevelPill>
  );
};

const SOLO_ACCURACY_KEY = [
  { band: "high", label: "Green", rule: "= > 80% (e.g., 3/3, 4/5, 9/10)" },
  { band: "mid", label: "Yellow", rule: "= 30 to 80%" },
  { band: "low", label: "Red", rule: "= < 30%" },
];

/** The legend for the Solo Accuracy pill's three bands (frame node 4729:69745). */
export const SoloAccuracyKey = () => (
  <KeyRow data-testid="solo-accuracy-key">
    <KeyTitle>Solo Accuracy Key:</KeyTitle>
    <KeyItems>
      {SOLO_ACCURACY_KEY.map(({ band, label, rule }) => (
        <KeyItem key={band}>
          <LevelPill $level={SOLO_BAND_LEVEL[band]}>{label}</LevelPill>
          <KeyRule>{rule}</KeyRule>
        </KeyItem>
      ))}
    </KeyItems>
  </KeyRow>
);

const KeyRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  color: ${colors.nightlyWoods500};
  font-family: "Inter", sans-serif;
`;

const KeyTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
  white-space: nowrap;
`;

const KeyItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
`;

const KeyItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const KeyRule = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  white-space: nowrap;
`;
